
export const detectPriceConstraints = (searchTerms: string[]) => {
  const constraints: { maxPrice?: number; minPrice?: number; budgetType?: string } = {};
  
  // Join terms to handle multi-word price expressions
  const fullQuery = searchTerms.join(' ').toLowerCase();
  
  console.log('🔍 === PRICE DETECTION ===');
  console.log('Full query for price detection:', fullQuery);
  
  // Budget indicators
  const budgetKeywords = ['goedkoop', 'budget', 'voordelig', 'betaalbaar', 'goedkope', 'budgetvriendelijk'];
  const luxuryKeywords = ['luxe', 'duur', 'premium', 'high-end', 'exclusief'];
  
  // Enhanced price pattern detection
  console.log('🔍 Checking for price patterns...');
  
  // Pattern 1: "tot €200", "tot 200", "tot 200 euro"
  const totPatterns = [
    /tot\s*€?\s*(\d+)/g,
    /tot\s*(\d+)\s*(euro|eur|pp|per persoon)?/g
  ];
  
  // Pattern 2: "vanaf €200", "vanaf 200", "vanaf 200 euro"  
  const vanafPatterns = [
    /vanaf\s*€?\s*(\d+)/g,
    /vanaf\s*(\d+)\s*(euro|eur|pp|per persoon)?/g
  ];
  
  // Pattern 3: "onder €200", "onder de 200"
  const onderPatterns = [
    /onder\s*(?:de\s*)?€?\s*(\d+)/g,
    /onder\s*(?:de\s*)?(\d+)\s*(euro|eur|pp|per persoon)?/g
  ];
  
  // Pattern 4: "boven €200", "boven de 200"
  const bovenPatterns = [
    /boven\s*(?:de\s*)?€?\s*(\d+)/g,
    /boven\s*(?:de\s*)?(\d+)\s*(euro|eur|pp|per persoon)?/g
  ];
  
  // Pattern 5: "max €200", "maximaal 200"
  const maxPatterns = [
    /max(?:imaal)?\s*€?\s*(\d+)/g,
    /max(?:imaal)?\s*(\d+)\s*(euro|eur|pp|per persoon)?/g
  ];
  
  // Pattern 6: "min €200", "minimaal 200"
  const minPatterns = [
    /min(?:imaal)?\s*€?\s*(\d+)/g,
    /min(?:imaal)?\s*(\d+)\s*(euro|eur|pp|per persoon)?/g
  ];
  
  // Pattern 7: Just "€200" or "200 euro" without context
  const euroPatterns = [
    /€\s*(\d+)/g,
    /(\d+)\s*(euro|eur)(?!\s*tot|\s*vanaf)/g
  ];
  
  // Check "tot" patterns (max price)
  totPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(fullQuery)) !== null) {
      const price = parseInt(match[1]);
      if (price > 0) {
        console.log('✅ Found "tot" pattern:', match[0], '→ maxPrice:', price);
        constraints.maxPrice = price;
      }
    }
  });
  
  // Check "vanaf" patterns (min price)
  vanafPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(fullQuery)) !== null) {
      const price = parseInt(match[1]);
      if (price > 0) {
        console.log('✅ Found "vanaf" pattern:', match[0], '→ minPrice:', price);
        constraints.minPrice = price;
      }
    }
  });
  
  // Check "onder" patterns (max price)
  onderPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(fullQuery)) !== null) {
      const price = parseInt(match[1]);
      if (price > 0) {
        console.log('✅ Found "onder" pattern:', match[0], '→ maxPrice:', price);
        constraints.maxPrice = price;
      }
    }
  });
  
  // Check "boven" patterns (min price)
  bovenPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(fullQuery)) !== null) {
      const price = parseInt(match[1]);
      if (price > 0) {
        console.log('✅ Found "boven" pattern:', match[0], '→ minPrice:', price);
        constraints.minPrice = price;
      }
    }
  });
  
  // Check "max" patterns (max price)
  maxPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(fullQuery)) !== null) {
      const price = parseInt(match[1]);
      if (price > 0) {
        console.log('✅ Found "max" pattern:', match[0], '→ maxPrice:', price);
        constraints.maxPrice = price;
      }
    }
  });
  
  // Check "min" patterns (min price)
  minPatterns.forEach(pattern => {
    let match;
    while ((match = pattern.exec(fullQuery)) !== null) {
      const price = parseInt(match[1]);
      if (price > 0) {
        console.log('✅ Found "min" pattern:', match[0], '→ minPrice:', price);
        constraints.minPrice = price;
      }
    }
  });
  
  // Check general euro patterns (default to max price unless we already have min/max)
  if (!constraints.maxPrice && !constraints.minPrice) {
    euroPatterns.forEach(pattern => {
      let match;
      while ((match = pattern.exec(fullQuery)) !== null) {
        const price = parseInt(match[2] || match[1]);
        if (price > 0) {
          console.log('✅ Found general euro pattern:', match[0], '→ defaulting to maxPrice:', price);
          constraints.maxPrice = price;
        }
      }
    });
  }
  
  // Budget type detection
  if (budgetKeywords.some(keyword => fullQuery.includes(keyword))) {
    constraints.budgetType = 'budget';
    console.log('✅ Detected budget type');
    // Set reasonable budget max if no specific price mentioned
    if (!constraints.maxPrice && !constraints.minPrice) {
      constraints.maxPrice = 500; // Default budget max
      console.log('→ Applied default budget maxPrice: 500');
    }
  }
  
  if (luxuryKeywords.some(keyword => fullQuery.includes(keyword))) {
    constraints.budgetType = 'luxury';
    console.log('✅ Detected luxury type');
    // Set minimum for luxury if no specific price mentioned
    if (!constraints.minPrice && !constraints.maxPrice) {
      constraints.minPrice = 1000; // Default luxury min
      console.log('→ Applied default luxury minPrice: 1000');
    }
  }
  
  console.log('🎯 Final price constraints:', constraints);
  return Object.keys(constraints).length > 0 ? constraints : null;
};

export const applyPriceFilter = (dbQuery: any, priceConstraints: any) => {
  if (!priceConstraints) return dbQuery;
  
  console.log('💰 === APPLYING PRICE FILTERS ===');
  console.log('Price constraints to apply:', priceConstraints);
  
  if (priceConstraints.maxPrice !== undefined) {
    console.log('🔽 Applying maxPrice filter: ≤', priceConstraints.maxPrice);
    dbQuery = dbQuery.lte('price', priceConstraints.maxPrice);
  }
  
  if (priceConstraints.minPrice !== undefined) {
    console.log('🔼 Applying minPrice filter: ≥', priceConstraints.minPrice);
    dbQuery = dbQuery.gte('price', priceConstraints.minPrice);
  }
  
  console.log('✅ Price filters applied successfully');
  return dbQuery;
};
